import React, { useState, useEffect } from "react";
import { Card, CardBody, Col, Container, Row, Nav, NavItem, NavLink, Button, Input, Alert } from "reactstrap";
import Breadcrumbs from "../../Components/Common/Breadcrumb";
import classnames from "classnames";
import axios from "axios";

// Add keyframes for linear progress animation
const style = document.createElement('style');
style.textContent = `
  @keyframes linearProgress {
    0% {
      transform: translateX(-100%);
    }
    50% {
      transform: translateX(0%);
    }
    100% {
      transform: translateX(100%);
    }
  }
`;
if (!document.getElementById('linear-progress-styles')) {
  style.id = 'linear-progress-styles';
  document.head.appendChild(style);
}

interface Notification {
  id: number;
  title: string;
  message: string;
  from_firstname: string;
  from_fullname: string;
  is_read: boolean;
  created_at: string;
}

const NotificationsList = () => {
  // Meta title
  document.title = "Notifications | BizServe";

  // State management
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);
  const [alert, setAlert] = useState<{ type: string; message: string; show: boolean }>({
    type: "success",
    message: "",
    show: false,
  });

  // Fetch notifications
  const fetchNotifications = async (page = 1, unreadOnly = false) => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: "20",
      });

      if (unreadOnly) {
        params.append("unread_only", "true");
      }

      const response = await axios.get(`/notifications/user_notifications?${params}`);

      if (response.status === "SENT") {
        setNotifications(response.notifications);
        setTotalPages(response.total_pages);
        setTotalCount(response.count);
        setUnreadCount(response.unread_count);
        setCurrentPage(page);
      }
    } catch (error: any) {
      console.error("Error fetching notifications:", error);
      showAlert("error", "Failed to fetch notifications");
    } finally {
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    fetchNotifications(1, activeTab === "unread");
  }, [activeTab]);

  // Show alert helper
  const showAlert = (type: string, message: string) => {
    setAlert({ type, message, show: true });
    setTimeout(() => setAlert({ type: "", message: "", show: false }), 4000);
  };

  // Mark notification as read
  const markAsRead = async (notificationId: number) => {
    try {
      await axios.patch(`/notifications/${notificationId}/mark_read/`);
      fetchNotifications(currentPage, activeTab === "unread");
      showAlert("success", "Notification marked as read");
    } catch (error) {
      console.error("Error marking as read:", error);
      showAlert("error", "Failed to mark as read");
    }
  };

  // Mark all as read
  const markAllAsRead = async () => {
    try {
      const response = await axios.post("/notifications/mark_all_read/");
      if (response.status === "SUCCESS") {
        fetchNotifications(currentPage, activeTab === "unread");
        showAlert("success", `${response.marked_count} notifications marked as read`);
      }
    } catch (error) {
      console.error("Error marking all as read:", error);
      showAlert("error", "Failed to mark all as read");
    }
  };

  // Delete notification
  const deleteNotification = async (notificationId: number) => {
    if (!window.confirm("Are you sure you want to delete this notification?")) {
      return;
    }

    try {
      await axios.delete(`/notifications/${notificationId}/`);
      fetchNotifications(currentPage, activeTab === "unread");
      showAlert("success", "Notification deleted");
    } catch (error) {
      console.error("Error deleting notification:", error);
      showAlert("error", "Failed to delete notification");
    }
  };

  // Delete all read notifications
  const deleteAllRead = async () => {
    if (!window.confirm("Are you sure you want to delete all read notifications?")) {
      return;
    }

    try {
      const response = await axios.post("/notifications/delete_all_read/");
      if (response.status === "SUCCESS") {
        fetchNotifications(currentPage, activeTab === "unread");
        showAlert("success", `${response.deleted_count} notifications deleted`);
      }
    } catch (error) {
      console.error("Error deleting notifications:", error);
      showAlert("error", "Failed to delete notifications");
    }
  };

  // Format time ago
  const timeAgo = (dateString: string) => {
    const now = new Date();
    const created = new Date(dateString);
    const diff = Math.floor((now.getTime() - created.getTime()) / 1000);

    if (diff < 60) return `${diff} seconds ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)} minutes ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} hours ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)} days ago`;
    return created.toLocaleDateString();
  };

  // Filter notifications by search
  const filteredNotifications = notifications.filter((notification) => {
    const matchesSearch =
      searchTerm === "" ||
      notification.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      notification.message.toLowerCase().includes(searchTerm.toLowerCase());

    if (activeTab === "all") return matchesSearch;
    if (activeTab === "unread") return matchesSearch && !notification.is_read;
    if (activeTab === "read") return matchesSearch && notification.is_read;

    return matchesSearch;
  });

  // Pagination controls
  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      fetchNotifications(page, activeTab === "unread");
    }
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          {/* Breadcrumb */}
          <Breadcrumbs title="BizServe" breadcrumbItem="Notifications" />

          {/* Alert */}
          {alert.show && (
            <Alert color={alert.type === "error" ? "danger" : "success"} toggle={() => setAlert({ ...alert, show: false })}>
              {alert.message}
            </Alert>
          )}

          <Row>
            <Col lg={12}>
              <Card style={{ position: 'relative', overflow: 'hidden' }}>
                {/* Linear Loading Indicator */}
                {loading && (
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '3px',
                    background: '#e9ecef',
                    overflow: 'hidden',
                    zIndex: 1000
                  }}>
                    <div style={{
                      height: '100%',
                      background: 'linear-gradient(90deg, #556ee6 0%, #3b5de7 50%, #556ee6 100%)',
                      animation: 'linearProgress 1.5s ease-in-out infinite',
                      transformOrigin: 'left center'
                    }} />
                  </div>
                )}
                
                <CardBody>
                  {/* Header with stats and actions */}
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <div>
                      <h4 className="card-title mb-1">Notifications</h4>
                      <p className="text-muted mb-0">
                        {totalCount} total, {unreadCount} unread
                      </p>
                    </div>
                    <div className="d-flex gap-2">
                      <Button color="primary" size="sm" onClick={markAllAsRead} disabled={unreadCount === 0}>
                        <i className="bx bx-check-double me-1"></i>
                        Mark All Read
                      </Button>
                      <Button color="danger" size="sm" onClick={deleteAllRead}>
                        <i className="bx bx-trash me-1"></i>
                        Delete All Read
                      </Button>
                    </div>
                  </div>

                  {/* Search bar */}
                  <div className="mb-3">
                    <Input
                      type="text"
                      placeholder="Search notifications..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="form-control"
                    />
                  </div>

                  {/* Tabs */}
                  <Nav tabs className="nav-tabs-custom nav-justified mb-3">
                    <NavItem>
                      <NavLink
                        style={{ cursor: "pointer" }}
                        className={classnames({ active: activeTab === "all" })}
                        onClick={() => {
                          setActiveTab("all");
                          setCurrentPage(1);
                        }}
                      >
                        <span className="d-block d-sm-none">
                          <i className="fas fa-home"></i>
                        </span>
                        <span className="d-none d-sm-block">All</span>
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink
                        style={{ cursor: "pointer" }}
                        className={classnames({ active: activeTab === "unread" })}
                        onClick={() => {
                          setActiveTab("unread");
                          setCurrentPage(1);
                        }}
                      >
                        <span className="d-block d-sm-none">
                          <i className="far fa-envelope"></i>
                        </span>
                        <span className="d-none d-sm-block">
                          Unread {unreadCount > 0 && <span className="badge bg-danger ms-1">{unreadCount}</span>}
                        </span>
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink
                        style={{ cursor: "pointer" }}
                        className={classnames({ active: activeTab === "read" })}
                        onClick={() => {
                          setActiveTab("read");
                          setCurrentPage(1);
                        }}
                      >
                        <span className="d-block d-sm-none">
                          <i className="far fa-envelope-open"></i>
                        </span>
                        <span className="d-none d-sm-block">Read</span>
                      </NavLink>
                    </NavItem>
                  </Nav>

                  {/* Empty state */}
                  {!loading && filteredNotifications.length === 0 ? (
                    <div className="text-center py-5">
                      <i className="bx bx-bell-off display-4 text-muted"></i>
                      <h5 className="mt-3 text-muted">No notifications found</h5>
                    </div>
                  ) : null}
                  
                  {/* Notifications list */}
                  {!loading && filteredNotifications.length > 0 && (
                    <>
                      {/* Notifications list */}
                      <div className="list-group">
                        {filteredNotifications.map((notification) => (
                          <div
                            key={notification.id}
                            className={`list-group-item ${!notification.is_read ? "bg-light" : ""}`}
                            style={{ borderLeft: !notification.is_read ? "4px solid #556ee6" : "none" }}
                          >
                            <div className="d-flex">
                              <div className="flex-shrink-0 me-3">
                                <div className="avatar-xs">
                                  <span className="avatar-title bg-primary rounded-circle font-size-16">
                                    <i className="bx bx-bell"></i>
                                  </span>
                                </div>
                              </div>
                              <div className="flex-grow-1">
                                <div className="d-flex justify-content-between">
                                  <div>
                                    <h6 className="mb-1">{notification.title}</h6>
                                    <p className="text-muted mb-1">{notification.message}</p>
                                    <p className="text-muted mb-0 font-size-12">
                                      <i className="mdi mdi-account-circle me-1"></i>
                                      {notification.from_fullname || "System"} • <i className="mdi mdi-clock-outline me-1"></i>
                                      {timeAgo(notification.created_at)}
                                    </p>
                                  </div>
                                  <div className="flex-shrink-0 ms-3">
                                    <div className="d-flex gap-2">
                                      {!notification.is_read && (
                                        <Button
                                          color="success"
                                          size="sm"
                                          onClick={() => markAsRead(notification.id)}
                                          title="Mark as read"
                                        >
                                          <i className="bx bx-check"></i>
                                        </Button>
                                      )}
                                      <Button
                                        color="danger"
                                        size="sm"
                                        onClick={() => deleteNotification(notification.id)}
                                        title="Delete"
                                      >
                                        <i className="bx bx-trash"></i>
                                      </Button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Pagination */}
                      {totalPages > 1 && (
                        <div className="d-flex justify-content-between align-items-center mt-4">
                          <div className="text-muted">
                            Page {currentPage} of {totalPages}
                          </div>
                          <div>
                            <Button
                              color="primary"
                              size="sm"
                              onClick={() => handlePageChange(currentPage - 1)}
                              disabled={currentPage === 1}
                              className="me-2"
                            >
                              <i className="bx bx-chevron-left"></i> Previous
                            </Button>
                            <Button
                              color="primary"
                              size="sm"
                              onClick={() => handlePageChange(currentPage + 1)}
                              disabled={currentPage === totalPages}
                            >
                              Next <i className="bx bx-chevron-right"></i>
                            </Button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default NotificationsList;

